/* ══════════════════════════════════════════════════════════════════════
   POLICY INSIGHTS — the access-graph card.

   Renders the whole tree at once (no collapsing): readability comes from
   zoom, search, and the legend filters instead. Layout is derived from
   the data's parentId pointers, so a branch that is 3 levels deep and
   one that is 4 levels deep both render correctly without the hierarchy
   being hardcoded here.

   Filtering never changes the layout — matching nodes stay lit and the
   rest drop to low opacity, so nothing moves under the user's cursor.
   ══════════════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "next-themes";
import {
  Boxes,
  FileText,
  IdCard,
  Search,
  User,
  UserCog,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  Background,
  Handle,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  PAG_KIND_META,
  pagNetKindColor,
  pagNetRiskColor,
  type PagNodeKind,
  type PagSeverity,
  type PagTreeNode,
} from "../data/pagTypes";
import { NetworkLegend } from "./NetworkGraph/NetworkLegend";
import { SURFACE as NET_SURFACE } from "./NetworkGraph/networkGraphVisuals";
import {
  GraphFullscreenControl,
  GraphZoomControls,
  useGraphFullscreen,
} from "./NetworkGraph/GraphControls";
import { markGlowShadow } from "@/app/components/ChartsComponent/chartUtils";
import {
  ChartEmptyState,
  ChartLoadingState,
} from "@/app/components/ChartsComponent/ChartStates";

/* ══════════════════════════════════════════════════════════════════════
   DESIGN TOKENS

   Every dimension, colour and type size the graph draws with lives here,
   so the visual language can be retuned in one place instead of hunting
   magic numbers through the render code. Nothing below is per-node or
   per-dataset — the layout stays fully derived from the data.
   ══════════════════════════════════════════════════════════════════════ */

/** Node box — compact enterprise-dashboard chip. */
const NODE = {
  width: 216,
  height: 28,
  radius: 6,
  paddingX: 8,
  /** Space between icon, label and badge. */
  gap: 7,
  iconSize: 12,
  fontSize: 11,
  borderWidth: 1,
  /** Border/handle alpha suffixes on the kind colour (8-digit hex).
   *  Lit is full strength now that the neon sits behind it: a glow on a
   *  55% stroke lit an edge fainter than its own halo, which reads as a
   *  smudge rather than as a lit outline. */
  borderAlpha: "ff",
  dimBorderAlpha: "2e", // ~18%
} as const;

/** Count badge — subtle, right-aligned, never taller than the node. */
const BADGE = {
  fontSize: 9,
  paddingX: 5,
  height: 14,
  radius: 999,
  bgAlpha: "1f", // ~12%
} as const;

/** Layout spacing. Column pitch is derived so a wider node never collides
 *  with the next level. */
const LAYOUT = {
  /** Horizontal gap between one hierarchy level and the next. */
  colGap: 88,
  /** Vertical pitch between two adjacent leaf rows. */
  rowPitch: 38,
  /** Extra rows inserted between sibling subtrees so groups read as
   *  groups. Fractional: runs of plain leaves stay tight. */
  groupSpacer: 0.45,
} as const;

const COL_PITCH = NODE.width + LAYOUT.colGap;

/** Connections — thin and neutral so nodes stay the loudest thing on the
 *  canvas. Deliberately NOT tinted per node kind: colouring hundreds of
 *  edges turns the fan-outs into visual noise. */
/* Matched to the Network tab's edges, and for the same reason: at 1px and
   0.05 dim opacity the connections read as absent rather than as quiet. */
const EDGE = {
  width: 1.4,
  litOpacity: 1,
  dimOpacity: 0.3,
  color: { light: "#7C8BA1", dark: "rgba(163,178,199,0.78)" },
} as const;

/** Canvas surfaces per theme. */
const SURFACE = {
  light: {
    canvas: "#FFFFFF",
    grid: "#E8EDF4",
    nodeBg: "#FFFFFF",
    nodeText: "#0F172A",
    panel: "rgba(255,255,255,0.95)",
    panelBorder: "#E2E8F0",
    muted: "#94A3B8",
    shadow: "0 1px 2px rgba(15,23,42,0.06)",
  },
  dark: {
    canvas: "#0A1120",
    grid: "#1A2438",
    nodeBg: "#111A2C",
    nodeText: "#E2E8F0",
    panel: "rgba(13,19,34,0.92)",
    panelBorder: "rgba(255,255,255,0.12)",
    muted: "#64748B",
    shadow: "0 1px 2px rgba(0,0,0,0.4)",
  },
} as const;

/** Icon per node kind. Reuses lucide (an approved library) and mirrors the
 *  identity each level represents. */
const KIND_ICON: Record<PagNodeKind, LucideIcon> = {
  application: Boxes,
  account: IdCard,
  user: User,
  group: Users,
  role: UserCog,
  policy: FileText,
};

/* ── Intro camera move ─────────────────────────────────────────────────
   "Overview first, zoom and filter, details-on-demand": the graph opens
   zoomed-to-fit so its whole shape reads at a glance, holds there long
   enough to be understood, then flies in to the root at a fixed zoom.
   React Flow drives the flight with d3-zoom's interpolateZoom, so the
   path arcs out and back in rather than scaling linearly. */

/** Zoom level the flight lands on — wide enough to keep several columns
 *  of the hierarchy on screen rather than filling the frame with one. */
const FOCUS_ZOOM = 0.6;
/** Where the root sits across the viewport once landed. Centring it (0.5)
 *  would waste the whole left half, since the tree only grows rightwards. */
const ROOT_X_FRACTION = 0.22;
/** Delay before the first fit, so React Flow has measured the nodes. */
const MEASURE_MS = 80;
/** How long the overview is held before the camera starts moving. */
const OVERVIEW_HOLD_MS = 700;
/** Flight time from overview to the focused node. */
const FLY_MS = 900;
const FIT_PADDING = 0.18;

/* ── Layout ─────────────────────────────────────────────────────────── */

/**
 * Tidy left-to-right tree: x comes from depth, y from a running leaf
 * cursor. Each leaf takes the next free row and every parent centres on
 * the midpoint of its first and last child, which keeps sibling groups
 * visually attached without an external layout dependency.
 *
 * A fractional spacer is inserted between adjacent sibling SUBTREES (a
 * child that has children of its own, next to another child), so each
 * role-and-its-policies block is visually separable. Consecutive plain
 * leaves get no spacer, which keeps density high and avoids the wide
 * empty bands a fixed per-child gap would create.
 *
 * Guards against malformed data: a node whose parentId is missing from the
 * set becomes a root, and a node reached twice returns its existing y
 * rather than being laid out again (so a cycle terminates).
 */
function layoutTree(treeNodes: PagTreeNode[]): Map<string, { x: number; y: number }> {
  const byId = new Map(treeNodes.map((n) => [n.id, n]));
  const childrenOf = new Map<string, PagTreeNode[]>();
  const roots: PagTreeNode[] = [];

  for (const n of treeNodes) {
    if (n.parentId && byId.has(n.parentId) && n.parentId !== n.id) {
      const kids = childrenOf.get(n.parentId);
      if (kids) kids.push(n);
      else childrenOf.set(n.parentId, [n]);
    } else {
      roots.push(n);
    }
  }

  const positions = new Map<string, { x: number; y: number }>();
  const hasKids = (n: PagTreeNode) => (childrenOf.get(n.id)?.length ?? 0) > 0;
  let leafCursor = 0;

  const place = (node: PagTreeNode, depth: number): number => {
    const existing = positions.get(node.id);
    if (existing) return existing.y;

    const kids = childrenOf.get(node.id) ?? [];
    const x = depth * COL_PITCH;

    let y: number;
    if (kids.length === 0) {
      y = leafCursor * LAYOUT.rowPitch;
      leafCursor += 1;
      positions.set(node.id, { x, y });
    } else {
      // Reserve the slot before recursing so a cycle sees a placed node.
      positions.set(node.id, { x, y: leafCursor * LAYOUT.rowPitch });

      const ys: number[] = [];
      kids.forEach((kid, i) => {
        if (i > 0 && (hasKids(kids[i - 1]) || hasKids(kid))) {
          leafCursor += LAYOUT.groupSpacer;
        }
        ys.push(place(kid, depth + 1));
      });

      y = (ys[0] + ys[ys.length - 1]) / 2;
      positions.set(node.id, { x, y });
    }
    return y;
  };

  roots.forEach((r) => place(r, 0));
  return positions;
}

/* ── Node ───────────────────────────────────────────────────────────── */

function PagFlowNode({ data }: { data: any }) {
  const kind = data.kind as PagNodeKind;
  /* Same palette as the Network tab. These are two views of one dataset,
     so a node kind must not change colour between them — and the legend
     they now share would otherwise be wrong on one of the two. */
  const dim: boolean = data.dim;
  const isDark: boolean = data.isDark;
  const color = pagNetKindColor(kind, isDark);
  const sev = data.severity as PagSeverity | null;

  const tooltip = [
    `${PAG_KIND_META[kind].label.replace(/s$/, "")}: ${data.label}`,
    sev ? `Severity: ${sev}` : null,
    ...Object.entries(data.meta ?? {}).map(([k, v]) => `${k}: ${v}`),
  ]
    .filter(Boolean)
    .join("\n");

  const surface = isDark ? SURFACE.dark : SURFACE.light;
  const Icon = KIND_ICON[kind];

  /* Severity reads as the label's colour rather than an extra dot: it keeps
     the chip to one row of content and makes a risky policy legible at the
     zoom levels this graph is actually viewed at. */
  const labelColor = sev ? pagNetRiskColor(sev, isDark) : surface.nodeText;

  /* Handles are invisible anchor points — the connection is communicated by
     the edge itself, so a visible dot on every node would just add clutter
     at this density. */
  const handleStyle = {
    width: 1,
    height: 1,
    minWidth: 1,
    minHeight: 1,
    background: "transparent",
    border: "none",
    opacity: 0,
  } as const;

  return (
    <div
      title={tooltip}
      style={{
        width: NODE.width,
        height: NODE.height,
        display: "flex",
        alignItems: "center",
        gap: NODE.gap,
        padding: `0 ${NODE.paddingX}px`,
        borderRadius: NODE.radius,
        background: surface.nodeBg,
        border: `${NODE.borderWidth}px solid ${color}${dim ? NODE.dimBorderAlpha : NODE.borderAlpha}`,
        /* DOM counterpart of the Network tab's canvas halo, and the same
           two layers -- tight core, wide bleed -- plus the inner shadow
           that lights a box from its own edge.

           A box-shadow rather than the SVG `markGlow` filter because this
           node is an HTML box with a label inside it: a filter follows the
           painted silhouette and would blur the text along with the
           border. That is precisely why chartUtils keeps the two helpers
           apart, and this is the case the DOM one exists for.

           A dimmed node stays unlit: the glow is what marks the path in
           focus, so lighting the ones faded out of it would say nothing. */
        boxShadow: dim ? "none" : markGlowShadow(color),
        opacity: dim ? 0.2 : 1,
        transition: "opacity 180ms ease, border-color 180ms ease",
        boxSizing: "border-box",
      }}
    >
      {data.hasParent && <Handle type="target" position={Position.Left} style={handleStyle} />}
      {data.hasChildren && <Handle type="source" position={Position.Right} style={handleStyle} />}

      <Icon
        size={NODE.iconSize}
        color={color}
        strokeWidth={2}
        style={{ flexShrink: 0 }}
        aria-hidden
      />

      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: NODE.fontSize,
          fontWeight: 500,
          lineHeight: 1.2,
          letterSpacing: 0.1,
          color: labelColor,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {data.label}
      </span>

      {data.childCount > 0 && (
        <span
          style={{
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            height: BADGE.height,
            padding: `0 ${BADGE.paddingX}px`,
            fontSize: BADGE.fontSize,
            fontWeight: 600,
            lineHeight: 1,
            borderRadius: BADGE.radius,
            background: `${color}${BADGE.bgAlpha}`,
            color,
          }}
        >
          {data.childCount}
        </span>
      )}
    </div>
  );
}

const pagNodeTypes = { pagNode: PagFlowNode };

/* ── Main ───────────────────────────────────────────────────────────── */

/** `loading` is not optional dressing: clicking Filter refetches, and
 *  without it this tab sat showing the PREVIOUS application's tree with no
 *  sign anything was happening -- so a slow response read as a filter that
 *  had not worked. The Network tab already took this prop; this one did
 *  not exist on it at all. */
export function PolicyInsightsGraph({
  treeNodes,
  loading = false,
}: {
  treeNodes: PagTreeNode[];
  loading?: boolean;
}) {
  const { theme } = useTheme();
  const isDark = theme !== "light";

  const containerRef = useRef<HTMLDivElement>(null);
  const [flow, setFlow] = useState<ReactFlowInstance | null>(null);
  const { isFullscreen, toggle: toggleFullscreen } = useGraphFullscreen(containerRef);
  const [search, setSearch] = useState("");
  /* Hide-sets, not show-sets. The Network tab's legend works by switching
     a kind OFF, and both tabs now share that legend, so the semantics have
     to match or the same click would mean opposite things per tab. */
  const [hiddenKinds, setHiddenKinds] = useState<Set<PagNodeKind>>(new Set());
  const [hiddenRisks, setHiddenRisks] = useState<Set<PagSeverity>>(new Set());

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  /* Positions depend only on the data, so they are computed once per
     dataset — filtering must never trigger a re-layout. */
  const base = useMemo(() => {
    const positions = layoutTree(treeNodes);
    const childCount = new Map<string, number>();
    treeNodes.forEach((n) => {
      if (n.parentId) childCount.set(n.parentId, (childCount.get(n.parentId) ?? 0) + 1);
    });

    const flowNodes: Node[] = treeNodes.map((n) => ({
      id: n.id,
      type: "pagNode",
      position: positions.get(n.id) ?? { x: 0, y: 0 },
      data: {
        label: n.label,
        kind: n.kind,
        severity: n.severity ?? null,
        meta: n.meta,
        hasParent: !!n.parentId,
        hasChildren: (childCount.get(n.id) ?? 0) > 0,
        childCount: childCount.get(n.id) ?? 0,
        dim: false,
        isDark,
      },
    }));

    /* Bezier rather than smoothstep: the fan-out from one parent to many
       children reads as a bundle of curves instead of a ladder of right
       angles, which is what keeps dense levels followable. */
    const edgeColor = isDark ? EDGE.color.dark : EDGE.color.light;
    const flowEdges: Edge[] = treeNodes
      .filter((n) => n.parentId && positions.has(n.parentId))
      .map((n) => ({
        id: `e|${n.parentId}|${n.id}`,
        source: n.parentId as string,
        target: n.id,
        type: "default",
        style: {
          stroke: edgeColor,
          strokeWidth: EDGE.width,
          opacity: EDGE.litOpacity,
        },
      }));

    return { flowNodes, flowEdges };
  }, [treeNodes, isDark]);

  /* A node stays lit unless something switches it off. Risk applies to
     policy nodes only — it is the only kind that carries one, and dimming
     every other node the moment a risk is hidden would read as a bug. */
  const isLit = useCallback(
    (n: { kind: PagNodeKind; severity: PagSeverity | null; label: string }) => {
      if (hiddenKinds.has(n.kind)) return false;
      if (n.kind === "policy" && hiddenRisks.has(n.severity ?? "Uncategorized")) return false;
      const q = search.trim().toLowerCase();
      if (q && !n.label.toLowerCase().includes(q)) return false;
      return true;
    },
    [hiddenKinds, hiddenRisks, search],
  );

  /* The dataset the current positions were laid out for. Compared by
     identity: treeNodes is memoised upstream, so a new reference means
     genuinely new data rather than a re-render. */
  const laidOutForRef = useRef<PagTreeNode[] | null>(null);

  useEffect(() => {
    const litIds = new Set<string>();
    base.flowNodes.forEach((n) => {
      const d = n.data as any;
      if (isLit({ kind: d.kind, severity: d.severity, label: d.label })) litIds.add(n.id);
    });

    const freshData = laidOutForRef.current !== treeNodes;
    laidOutForRef.current = treeNodes;

    setNodes((prev) => {
      /* This effect re-runs on every filter, search and theme change, and
         it rebuilds each node from `base` -- which carries the COMPUTED
         position. Taking that position verbatim would snap a dragged node
         home the moment anything else moved, so a placement the user made
         is carried across instead.

         New data is the exception: the whole tree re-lays out then, and a
         node left behind at a coordinate from the previous layout reads as
         stuck rather than placed. */
      const placed = freshData
        ? new Map<string, { x: number; y: number }>()
        : new Map(prev.map((n) => [n.id, n.position]));

      return base.flowNodes.map((n) => ({
        ...n,
        position: placed.get(n.id) ?? n.position,
        data: { ...(n.data as any), dim: !litIds.has(n.id), isDark },
      }));
    });
    setEdges(
      base.flowEdges.map((e) => {
        const on = litIds.has(e.source) && litIds.has(e.target);
        return {
          ...e,
          style: { ...e.style, opacity: on ? EDGE.litOpacity : EDGE.dimOpacity },
        };
      }),
    );
  }, [base, isLit, isDark, setNodes, setEdges, treeNodes]);

  /* The node the intro flight lands on: the tree's root, which in a
     left-to-right layout is where reading starts. Centre point, not the
     top-left corner React Flow stores. */
  const focusPoint = useMemo(() => {
    const root =
      base.flowNodes.find((n) => !(n.data as any).hasParent) ?? base.flowNodes[0];
    if (!root) return null;
    return {
      x: root.position.x + NODE.width / 2,
      y: root.position.y + NODE.height / 2,
    };
  }, [base.flowNodes]);

  /* Honour the OS "reduce motion" setting — the flight is decorative, so
     those users get the destination without the travel. */
  const reduceMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  /* Cancels a pending flight once the user grabs the canvas themselves —
     an animation that fights the cursor is worse than no animation. React
     Flow passes a null event for its own programmatic moves, so only real
     pointer input counts. */
  const flightCancelled = useRef(false);

  /* Runs on mount and whenever the dataset changes (a different
     application), so switching apps replays overview -> fly-in. */
  useEffect(() => {
    if (!flow || !focusPoint) return;
    flightCancelled.current = false;

    const timers: ReturnType<typeof setTimeout>[] = [];

    /* setCenter puts a flow coordinate at the middle of the viewport, so
       landing the root at ROOT_X_FRACTION means aiming at a point that
       far to its right. Viewport width converts to flow units by dividing
       by the zoom being flown to. */
    const aimPoint = () => {
      const width = containerRef.current?.getBoundingClientRect().width ?? 0;
      const shift = width ? (0.5 - ROOT_X_FRACTION) * (width / FOCUS_ZOOM) : 0;
      return { x: focusPoint.x + shift, y: focusPoint.y };
    };

    timers.push(
      setTimeout(() => {
        // Stage 1 — the overview, with no travel of its own.
        flow.fitView({ padding: FIT_PADDING, duration: 0 });

        if (reduceMotion) {
          const { x, y } = aimPoint();
          flow.setCenter(x, y, { zoom: FOCUS_ZOOM, duration: 0 });
          return;
        }

        // Stage 2 — hold, then fly in.
        timers.push(
          setTimeout(() => {
            if (flightCancelled.current) return;
            const { x, y } = aimPoint();
            flow.setCenter(x, y, { zoom: FOCUS_ZOOM, duration: FLY_MS });
          }, OVERVIEW_HOLD_MS),
        );
      }, MEASURE_MS),
    );

    return () => timers.forEach(clearTimeout);
  }, [flow, focusPoint, reduceMotion]);

  const toggleKind = (k: PagNodeKind) =>
    setHiddenKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const toggleRisk = (s: PagSeverity) =>
    setHiddenRisks((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const surface = isDark ? SURFACE.dark : SURFACE.light;
  const panelBg = surface.panel;
  const panelBorder = surface.panelBorder;

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg"
      style={{
        height: isFullscreen ? "100vh" : "62vh",
        minHeight: 460,
        background: surface.canvas,
        border: `1px solid ${panelBorder}`,
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={pagNodeTypes}
        onInit={setFlow}
        onMoveStart={(event) => {
          // Truthy event == real pointer/wheel input, so the user has taken
          // over and the pending intro flight must not yank them back.
          if (event) flightCancelled.current = true;
        }}
        nodesDraggable
        nodesConnectable={false}
        minZoom={0.05}
        maxZoom={2.5}
        fitView
        fitViewOptions={{ padding: FIT_PADDING }}
        proOptions={{ hideAttribution: true }}
      >
        {/* Very low-contrast dot grid: enough to read as a canvas, not
            enough to compete with the nodes. */}
        <Background color={surface.grid} gap={22} size={1} />
      </ReactFlow>

      <GraphFullscreenControl
        isFullscreen={isFullscreen}
        onToggle={toggleFullscreen}
        isDark={isDark}
      />

      <GraphZoomControls
        isDark={isDark}
        onZoomIn={() => flow?.zoomIn({ duration: 200 })}
        onZoomOut={() => flow?.zoomOut({ duration: 200 })}
        onFit={() => flow?.fitView({ padding: 0.18, duration: 400 })}
      />

      {/* Search — top right */}
      <div className="absolute right-3 top-3 z-10" style={{ width: 190 }}>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            style={{ color: isDark ? "#64748B" : "#94A3B8" }}
          />
          <input
            type="text"
            value={search}
            maxLength={120}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="w-full rounded-lg py-1.5 pl-8 text-xs outline-none"
            style={{
              paddingRight: search ? 26 : 10,
              background: panelBg,
              border: `1px solid ${panelBorder}`,
              color: isDark ? "#E2E8F0" : "#0F172A",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              title="Clear search"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              style={{ color: isDark ? "#64748B" : "#94A3B8" }}
            >
              <X className="size-3" />
            </button>
          )}
        </div>
      </div>

      {/* The SAME legend component the Network tab renders, not a copy of
          it — the two tabs are one dataset, so their key cannot be allowed
          to drift. Only the placement differs: the search box owns the
          top-right corner here, so the panel sits below it. */}
      <NetworkLegend
        surface={isDark ? NET_SURFACE.dark : NET_SURFACE.light}
        hiddenKinds={hiddenKinds}
        hiddenRisks={hiddenRisks}
        onToggleKind={toggleKind}
        onToggleRisk={toggleRisk}
      />

      {/* The canvas is never left blank or stale: a state always covers it,
          the same way the Network tab does it. */}
      {(loading || treeNodes.length === 0) && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center px-4"
          style={{ background: surface.canvas }}
        >
          {loading ? (
            <ChartLoadingState height={300} shape="radial" />
          ) : (
            <ChartEmptyState
              height={300}
              shape="radial"
              message="No applications carry access data yet, so there is nothing to map."
            />
          )}
        </div>
      )}
    </div>
  );
}
